import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  Star as StarIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { productAPI, menuUploadAPI } from '../services/api';

const ProductsPage = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (shopId) {
      fetchProducts();
    }
  }, [shopId]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await productAPI.getProducts(shopId);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(error.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setUploadError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      setUploadProgress(0);
      setUploadResult(null);

      const formData = new FormData();
      formData.append('menuImage', selectedFile);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await menuUploadAPI.uploadMenuImage(shopId, formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResult(response.data);
      
      // Refresh products list after successful upload
      setTimeout(() => {
        fetchProducts();
        handleCloseUploadDialog();
      }, 2000);
    } catch (error) {
      console.error('Error uploading menu:', error);
      setUploadError(error.response?.data?.message || 'Failed to upload menu image');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError('');
    setUploadResult(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError('');
    setUploadResult(null);
    setUploadProgress(0);
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return `₹${price.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        px: { xs: 1, sm: 2 }, 
        py: { xs: 1, sm: 2 },
        overflowX: 'hidden',
        maxWidth: '100%',
        width: '100%',
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/shops')}
          sx={{ mb: 2 }}
          fullWidth={isMobile}
        >
          {isMobile ? 'Back' : 'Back to Shops'}
        </Button>
        <Paper sx={{ 
          p: { xs: 2, sm: 4 }, 
          textAlign: 'center',
          width: '100%',
          overflow: 'hidden',
        }}>
          <Typography variant={isMobile ? "body1" : "h6"} color="error" gutterBottom>
            Error
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      px: { xs: 1, sm: 2 }, 
      py: { xs: 1, sm: 2 },
      overflowX: 'hidden',
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
    }}>
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between" 
        gap={2}
        mb={3}
        sx={{ width: '100%', overflow: 'hidden' }}
      >
        <Box 
          display="flex" 
          alignItems="center" 
          gap={{ xs: 1, sm: 2 }} 
          flex={1}
          minWidth={0}
          sx={{ overflow: 'hidden' }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/shops')}
            size={isMobile ? "medium" : "large"}
            sx={{ flexShrink: 0 }}
          >
            {isMobile ? 'Back' : 'Back to Shops'}
          </Button>
          <Typography 
            variant={isMobile ? "h5" : "h4"}
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Products
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleOpenUploadDialog}
          fullWidth={isMobile}
          size={isMobile ? "medium" : "large"}
          sx={{ flexShrink: 0 }}
        >
          Upload Menu Image
        </Button>
      </Box>

      {products.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <InventoryIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" gutterBottom>
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This shop doesn't have any products yet.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}>
          {products.map((product) => (
            <Card key={product._id} sx={{ 
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}>
              <CardContent sx={{ 
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'hidden',
              }}>
                <Box 
                  display="flex" 
                  gap={{ xs: 1.5, sm: 2 }} 
                  mb={2}
                  sx={{ 
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    alignItems: 'flex-start',
                  }}
                >
                  {product.images && product.images.length > 0 ? (
                    <Box
                      component="img"
                      src={product.images[0]}
                      alt={product.name}
                      sx={{
                        width: { xs: 70, sm: 80 },
                        minWidth: { xs: 70, sm: 80 },
                        maxWidth: { xs: 70, sm: 80 },
                        height: { xs: 70, sm: 80 },
                        objectFit: 'cover',
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: { xs: 70, sm: 80 },
                        minWidth: { xs: 70, sm: 80 },
                        maxWidth: { xs: 70, sm: 80 },
                        height: { xs: 70, sm: 80 },
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    >
                      <InventoryIcon color="disabled" />
                    </Box>
                  )}
                  <Box 
                    flex={1} 
                    minWidth={0}
                    sx={{ 
                      overflow: 'hidden',
                      maxWidth: { xs: 'calc(100% - 86px)', sm: 'calc(100% - 96px)' }, // image + gap
                      boxSizing: 'border-box',
                      width: 0, // Force flex to respect maxWidth
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      fontWeight="medium" 
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        maxWidth: '100%',
                        display: 'block',
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      }}
                    >
                      {product.name}
                    </Typography>
                    {product.shortDescription && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        display="block" 
                        sx={{ 
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                          maxWidth: '100%',
                        }}
                      >
                        {product.shortDescription}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Stack spacing={1.5}>
                  {product.category && product.category.length > 0 && (
                    <Box 
                      display="flex" 
                      flexWrap="wrap" 
                      gap={0.5}
                      sx={{ width: '100%', overflow: 'hidden' }}
                    >
                      {product.category.slice(0, 3).map((cat, idx) => (
                        <Chip
                          key={idx}
                          label={cat}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }
                          }}
                        />
                      ))}
                      {product.category.length > 3 && (
                        <Chip
                          label={`+${product.category.length - 3}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}

                  <Box 
                    display="flex" 
                    alignItems="flex-start" 
                    gap={{ xs: 1.5, sm: 2 }} 
                    flexWrap="wrap"
                    sx={{ width: '100%', overflow: 'hidden' }}
                  >
                    <Box sx={{ minWidth: 0, flex: { xs: '1 1 auto', sm: 'none' } }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Price
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="medium"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        }}
                      >
                        {formatPrice(product.price)}
                      </Typography>
                    </Box>
                    {product.offerPrice && (
                      <Box sx={{ minWidth: 0, flex: { xs: '1 1 auto', sm: 'none' } }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          Offer Price
                        </Typography>
                        <Typography 
                          variant="body1" 
                          color="success.main" 
                          fontWeight="medium"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          }}
                        >
                          {formatPrice(product.offerPrice)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box 
                    display="flex" 
                    flexWrap="wrap" 
                    gap={1}
                    sx={{ width: '100%' }}
                  >
                    <Chip
                      label={product.outofstock ? 'Out of Stock' : `Qty: ${product.quantity || 0}`}
                      size="small"
                      color={product.outofstock ? 'error' : 'default'}
                      sx={{ maxWidth: '100%' }}
                    />
                    {product.hideFromUser && (
                      <Chip label="Hidden" size="small" color="warning" />
                    )}
                    {product.todayDeals && (
                      <Chip label="Today's Deal" size="small" color="primary" />
                    )}
                    {!product.hideFromUser && !product.todayDeals && (
                      <Chip label="Active" size="small" color="success" />
                    )}
                    {product.star && (
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                        <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="body2" fontSize="0.75rem">
                          {product.star.toFixed(1)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Created At
                    </Typography>
                    <Typography variant="body2" fontSize="0.875rem">
                      {formatDate(product.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{ 
            overflowX: 'auto',
            width: '100%',
            maxWidth: '100%',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '8px', sm: '16px' },
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 80 }}>Image</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Name</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Price</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Offer Price</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Stock</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 80 }}>Rating</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product._id} hover>
                  <TableCell>
                    {product.images && product.images.length > 0 ? (
                      <Box
                        component="img"
                        src={product.images[0]}
                        alt={product.name}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: 'grey.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                        }}
                      >
                        <InventoryIcon color="disabled" />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {product.name}
                    </Typography>
                    {product.shortDescription && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {product.shortDescription.substring(0, 50)}
                        {product.shortDescription.length > 50 ? '...' : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {product.category && product.category.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {product.category.slice(0, 2).map((cat, idx) => (
                          <Chip
                            key={idx}
                            label={cat}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {product.category.length > 2 && (
                          <Chip
                            label={`+${product.category.length - 2}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatPrice(product.price)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {product.offerPrice ? (
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight="medium"
                      >
                        {formatPrice(product.offerPrice)}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.outofstock ? 'Out of Stock' : `Qty: ${product.quantity || 0}`}
                      size="small"
                      color={product.outofstock ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {product.hideFromUser && (
                        <Chip label="Hidden" size="small" color="warning" />
                      )}
                      {product.todayDeals && (
                        <Chip label="Today's Deal" size="small" color="primary" />
                      )}
                      {!product.hideFromUser && !product.todayDeals && (
                        <Chip label="Active" size="small" color="success" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {product.star ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="body2">{product.star.toFixed(1)}</Typography>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(product.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Menu Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
            width: { xs: '100%', sm: 'auto' },
          },
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}>
          Upload Menu Image (AI Processing)
        </DialogTitle>
        <DialogContent sx={{ 
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          overflowX: 'hidden',
        }}>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' }, wordBreak: 'break-word' }}>
                {uploadError}
              </Typography>
            </Alert>
          )}

          {uploadResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant={isMobile ? "body2" : "subtitle2"} gutterBottom sx={{ fontWeight: 'medium' }}>
                Menu processed successfully!
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                Created {uploadResult.categoriesCreated} categories and {uploadResult.productsCreated} products.
              </Typography>
            </Alert>
          )}

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                Processing menu image with AI... {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {!uploadResult && (
            <>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  p: { xs: 2, sm: 4 },
                  textAlign: 'center',
                  mb: 2,
                  cursor: 'pointer',
                  width: '100%',
                  boxSizing: 'border-box',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                {previewUrl ? (
                  <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src={previewUrl}
                      alt="Preview"
                      sx={{
                        maxWidth: '100%',
                        width: '100%',
                        height: 'auto',
                        maxHeight: { xs: 250, sm: 300 },
                        borderRadius: 1,
                        mb: 2,
                        objectFit: 'contain',
                      }}
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {selectedFile?.name}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      sx={{ mt: 1 }}
                      fullWidth={isMobile}
                      size={isMobile ? "small" : "medium"}
                    >
                      Change Image
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <ImageIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                    <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                      Click to upload menu image
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Supported formats: JPG, PNG, WEBP, GIF (Max 10MB)
                    </Typography>
                  </Box>
                )}
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' }, wordBreak: 'break-word' }}>
                  <strong>AI Processing:</strong> The system will automatically extract categories and products from your menu image using AI. 
                  Categories and products that don't exist will be created automatically.
                </Typography>
              </Alert>
            </>
          )}

          {uploadResult && (
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'medium' }}>
                Processing Results
              </Typography>
              <List sx={{ py: 0 }}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Categories Created"
                    secondary={uploadResult.categoriesCreated}
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      sx: { fontSize: { xs: '0.875rem', sm: '0.875rem' } }
                    }}
                    secondaryTypographyProps={{ 
                      variant: 'body1',
                      sx: { fontSize: { xs: '1rem', sm: '1rem' } }
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Products Created"
                    secondary={uploadResult.productsCreated}
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      sx: { fontSize: { xs: '0.875rem', sm: '0.875rem' } }
                    }}
                    secondaryTypographyProps={{ 
                      variant: 'body1',
                      sx: { fontSize: { xs: '1rem', sm: '1rem' } }
                    }}
                  />
                </ListItem>
              </List>
              {uploadResult.categories && uploadResult.categories.length > 0 && (
                <Box sx={{ mt: 2, width: '100%' }}>
                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium', fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                    Categories:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} sx={{ width: '100%' }}>
                    {uploadResult.categories.map((cat) => (
                      <Chip 
                        key={cat._id} 
                        label={cat.name} 
                        size="small"
                        sx={{ 
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {uploadResult.products && uploadResult.products.length > 0 && (
                <Box sx={{ mt: 2, width: '100%' }}>
                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium', fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                    Sample Products:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} sx={{ width: '100%' }}>
                    {uploadResult.products.slice(0, 5).map((prod) => (
                      <Chip
                        key={prod._id}
                        label={`${prod.name} - ₹${prod.price || 0}`}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          maxWidth: { xs: '100%', sm: 'auto' },
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: '200px', sm: 'none' },
                          }
                        }}
                      />
                    ))}
                    {uploadResult.products.length > 5 && (
                      <Chip
                        label={`+${uploadResult.products.length - 5} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={handleCloseUploadDialog} 
            disabled={uploading}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            {uploadResult ? 'Close' : 'Cancel'}
          </Button>
          {!uploadResult && (
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!selectedFile || uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              fullWidth={isMobile}
              size={isMobile ? "medium" : "large"}
            >
              {uploading ? 'Processing...' : 'Upload & Process'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;

